import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LogIn,
  LogOut,
  ShieldCheck,
  Film,
  ChevronDown,
  MessageSquare,
  GraduationCap,
  BookOpen,
  BookOpenCheck,
  Briefcase,
  Users,
  LayoutDashboard,
  FileText,
  Map,
  Newspaper,
  ClipboardList,
  Bike,
  Compass,
  Award,
  Sparkles,
  UserPlus,
  User,
  ShieldAlert,
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import useAudience, { AUDIENCES } from '../hooks/useAudience';
import { Button } from './ui/button';
import VehicleSelector from './VehicleSelector';
import { MobileMenu } from './MobileMenu';
import { GlobalSearchTrigger } from './GlobalSearchDialog';
import { B2BQuickShareButton } from './B2BQuickShare';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

const roleLabel = (role) => {
  if (role === 'admin') return 'Admin';
  if (role === 'enseignant') return 'Compte moniteur';
  return 'Élève';
};
const roleClass = (role) => {
  if (role === 'admin') return 'mc-tag mc-tag-red';
  if (role === 'enseignant') return 'mc-tag mc-tag-amber';
  return 'mc-tag mc-tag-blue';
};
const roleIcon = (role) => {
  if (role === 'admin') return ShieldCheck;
  if (role === 'enseignant') return GraduationCap;
  return null;
};

// ============================================================================
// NAV ÉLÈVE / GRAND PUBLIC (mode par défaut)
// ============================================================================
// PRIMARY_LINKS doublent désormais la BottomNav mobile (MAG → TUTOS → PLATEAU
// → ROUTE → LIVRET). L'ordre desktop suit la même logique pédagogique
// CODE 2 ROUES : Comprendre (Magazine, Vidéos) → Ressentir/Maîtriser (Plateau
// & Circulation) → CTA commercial (Formation) en dernier.
const PRIMARY_LINKS = [
  { to: '/magazine', label: 'Magazine', shortLabel: 'Mag', primary: true },
  { to: '/videos', label: 'Vidéos', shortLabel: 'Vidéos', primary: true },
  { to: '/shorts', label: 'Shorts', shortLabel: 'Shorts', primary: true },
  { to: '/epreuves', label: 'Plateau & Circulation', shortLabel: 'Plateau', primary: true },
  { to: '/permis-am', label: 'Permis AM', shortLabel: 'Permis AM', primary: true, badge: 'NEW' },
  { to: '/formation-125', label: 'Formation 125', shortLabel: 'F. 125', primary: true, badge: 'NEW' },
  { to: '/annuaire', label: 'Annuaire', shortLabel: 'Annuaire', primary: true, badge: 'NEW' },
  // { to: '/formation', label: 'Formation', primary: true }, // RETIRÉ (août 2026) : doublon avec Permis AM + Formation 125
];

// Menu déroulant "Trames & Livrets" — accès direct (visible en primary desktop nav)
// Regroupe le référentiel enseignant + les livrets élèves interactifs.
const TRAMES_LIVRETS_MENU = {
  trames: [
    { to: '/formations-2-roues',            label: 'Hub — Toutes les trames',         icon: Sparkles,       badge: 'NEW' },
    { to: '/plateau-progression',           label: 'Plateau A1/A2 · P1 P2 P3',        icon: Bike },
    { to: '/formation-125-progression',     label: 'Formation 125 · 7 h',             icon: Compass },
    { to: '/passerelle-a2-a-progression',   label: 'Passerelle A2 → A · 7 h',         icon: Award },
  ],
  livrets: [
    { to: '/livret-plateau',                label: 'Livret Plateau A1/A2',            icon: ClipboardList,  badge: 'Élève' },
    { to: '/livret-circulation',            label: 'Livret Circulation A1/A2',        icon: ClipboardList,  badge: 'Élève' },
    { to: '/livret-125',                    label: 'Livret Formation 125',            icon: ClipboardList,  badge: 'NEW' },
    { to: '/livret-am',                     label: 'Livret Permis AM (BSR)',          icon: ClipboardList,  badge: 'NEW' },
    { to: '/livret-passerelle-a2-a',        label: 'Livret Passerelle A2 → A',        icon: ClipboardList,  badge: 'NEW' },
    { to: '/mon-livret-moto',               label: 'Mon Livret Moto (accueil)',       icon: BookOpen },
  ],
};

/* PRIMARY_LINKS spécifiques futur enseignant CCS — MASQUÉ Phase 30
   (recentrage auto-école, plus de préparation CCS). */
const FUTUR_ENS_PRIMARY_LINKS = [];

const SECONDARY_LINKS = [
  { to: '/profil', label: 'Ma progression', authOnly: true },
  { to: '/parcours', label: 'Mon carnet', authOnly: true },
  { to: '/formations-2-roues', label: 'Trames pédagogiques 2-roues', badge: 'NEW' },
  { to: '/revision', label: 'Dossiers thématiques' },
  { to: '/lexique', label: 'Lexique du motard' },
  { to: '/mobilites', label: 'Mobilités urbaines' },
  { to: '/assr', label: 'ASSR / ASR', badge: 'Jeunes' },
  { to: '/newsletter', label: 'Newsletter' },
  { to: '/contact', label: 'Contact' },
];

/* SECONDARY_LINKS spécifiques au futur enseignant CCS — MASQUÉ Phase 30. */
const FUTUR_ENS_SECONDARY_LINKS = [
  { to: '/contact', label: 'Contact' },
];

// ============================================================================
// NAV ENSEIGNANT / ADMIN (mode STRICT — isolation totale, mai 2026)
// ============================================================================
// L'enseignant connecté (moniteur auto-école) voit sa propre Topbar avec
// uniquement les outils pédagogiques utiles pour donner cours aux élèves.
// Phase 30 (recentrage auto-école) : les liens CCS/prépa examen sont masqués.
const TEACHER_PRIMARY_LINKS = [
  { to: '/eleve/dashboard', label: 'Mon espace', icon: GraduationCap, primary: true },
];

// Dropdown "Cours" — UNIQUEMENT les supports pour donner cours aux élèves.
const TEACHER_COURS_LINKS = [
  { to: '/formations-2-roues', label: 'Trames pédagogiques (P1/P2/P3 · 125 · A2→A)', icon: BookOpenCheck, badge: 'NEW' },
  { to: '/enseignant/salle', label: 'Cours en salle', icon: BookOpen },
  { to: '/enseignant/terrain', label: 'Cours plateau (REMC)', icon: Map, badge: '8 fiches' },
  { to: '/enseignant/seance-circulation', label: 'Cours circulation (REMC)', icon: Map, badge: '26 fiches' },
  { to: '/enseignant/cours-circulation', label: 'Formations spécifiques 7h', icon: Briefcase, badge: 'A2→A · 125 · BSR' },
  { to: '/enseignant/exercices-plateau', label: '12 exercices plateau', icon: BookOpen, badge: 'Aide-mémoire' },
];

// Dropdown "Examen CCS" — MASQUÉ Phase 30 (recentrage auto-école).
const TEACHER_EXAMEN_PREPARATION = [];
const TEACHER_EXAMEN_DEROULE = [];

const TEACHER_EXAMEN_CONTENU = [
  { to: '/enseignant/livret-apprentissage', label: 'Livret REMC', icon: FileText },
  { to: '/enseignant/fondements-pedagogie', label: 'Fondements pédagogie', icon: GraduationCap },
];

// Rétro-compat (utilisé si le code y fait référence ailleurs)
const TEACHER_COURS_ELEVES = TEACHER_COURS_LINKS;
const TEACHER_COURS_CCS = [...TEACHER_EXAMEN_PREPARATION, ...TEACHER_EXAMEN_DEROULE, ...TEACHER_EXAMEN_CONTENU];

// Dropdown "Magazine ECSR" — contenus éditoriaux pour enseignant.
const TEACHER_MAGAZINE_LINKS = [
  { to: '/enquetes', label: 'Enquêtes & Témoignages', icon: Newspaper, badge: '31' },
  { to: '/magazine', label: 'Magazine pédagogique', icon: BookOpen },
  { to: '/enseignant/livret-apprentissage', label: 'Livret REMC', icon: FileText },
  { to: '/enseignant/fondements-pedagogie', label: 'Fondements pédagogie', icon: GraduationCap },
];

const TEACHER_PRO_LINKS = [
  { to: '/reseau', label: 'Forum', icon: Users, primary: true },
];

const isLinkActive = (pathname, to) => {
  if (to === '/') return pathname === '/';
  if (to === '/enseignant') return pathname === '/enseignant';
  return pathname === to || pathname.startsWith(to + '/');
};

/**
 * LoginDropdown — Bouton Connexion avec menu déroulant au survol/clic.
 * Propose les 3 types de connexion : Élève, Enseignant, Admin.
 * - Mode `compact` (prop) : visible md→lg (768–1023px) à droite près de la nav
 * - Mode normal : visible lg+ (≥1024px) à gauche près du logo
 */
const LOGIN_ROLES = [
  { key: 'eleve',      label: 'Élève',      desc: 'Code, flipbooks, quiz',    icon: User,          href: '/login?role=eleve',      testid: 'topbar-login-role-eleve' },
  { key: 'enseignant', label: 'Enseignant', desc: 'Trames, Back-office pro',  icon: GraduationCap, href: '/login?role=enseignant', testid: 'topbar-login-role-enseignant' },
  { key: 'admin',      label: 'Admin',      desc: 'Administration du site',   icon: ShieldAlert,   href: '/login?role=admin',      testid: 'topbar-login-role-admin' },
];

const LoginDropdown = ({ compact = false, testIdSuffix = '' }) => {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const closeTimerRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const scheduleClose = React.useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpen(false), 180);
  }, []);
  const cancelClose = React.useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const containerClass = compact
    ? 'relative hidden md:inline-flex lg:hidden flex-shrink-0'
    : 'relative hidden lg:inline-flex flex-shrink-0 ml-2';

  return (
    <div
      ref={containerRef}
      className={containerClass}
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      data-testid={`topbar-login-dropdown${testIdSuffix}`}
    >
      <Button
        variant="outline"
        size="sm"
        className="rounded-full gap-1.5 h-9"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`topbar-login-link${testIdSuffix}`}
      >
        <LogIn className="w-4 h-4" />
        <span>Connexion</span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </Button>

      {open && (
        <div
          role="menu"
          className={`absolute ${compact ? 'right-0' : 'left-0'} top-[calc(100%+6px)] z-50 w-64 rounded-xl border border-border bg-popover shadow-xl ring-1 ring-black/5 p-1.5 animate-in fade-in slide-in-from-top-1 duration-150`}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          data-testid={`topbar-login-menu${testIdSuffix}`}
        >
          <div className="px-3 pt-2 pb-1.5 text-[10px] font-black uppercase tracking-[0.14em] text-muted-foreground">
            Je me connecte en tant que…
          </div>
          {LOGIN_ROLES.map((r) => {
            const Icon = r.icon;
            return (
              <button
                key={r.key}
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); navigate(r.href); }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-[hsl(var(--primary))]/8 hover:text-[hsl(var(--primary))] focus:bg-[hsl(var(--primary))]/8 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30 transition-colors group"
                data-testid={r.testid}
              >
                <span className="w-8 h-8 rounded-lg bg-[hsl(var(--primary))]/8 text-[hsl(var(--primary))] grid place-items-center shrink-0 group-hover:bg-[hsl(var(--primary))]/15 transition">
                  <Icon className="w-4 h-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold leading-tight">{r.label}</span>
                  <span className="block text-[11px] text-muted-foreground leading-tight mt-0.5">{r.desc}</span>
                </span>
              </button>
            );
          })}
          <div className="border-t border-border/60 mt-1 pt-1">
            <Link
              to="/register"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] font-semibold text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/8 transition"
              data-testid={`topbar-login-dropdown-register${testIdSuffix}`}
            >
              <UserPlus className="w-3.5 h-3.5" /> Pas encore de compte ? Créer un compte
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};


const NavLink = ({ link, active }) => {
  // Cas spécial : Shorts — CTA rouge pilule animée pour maximiser la visibilité
  // sur desktop (demande utilisateur : "Shorts sera l'outil le plus utilisé").
  if (link.to === '/shorts') {
    return (
      <Link
        to={link.to}
        aria-current={active ? 'page' : undefined}
        className={`c2r-nav-link group relative px-2 lg:px-2.5 xl:px-3 py-1.5 rounded-full text-[12px] lg:text-[13px] font-black uppercase tracking-wider whitespace-nowrap inline-flex items-center gap-1.5 transition-all ${
          active
            ? 'bg-[hsl(var(--primary))] text-white shadow-lg shadow-[hsl(var(--primary))]/30'
            : 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-white border border-[hsl(var(--primary))]/40 hover:border-[hsl(var(--primary))]'
        }`}
        style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
        data-testid="topbar-nav-shorts"
      >
        <Film className="w-3.5 h-3.5" aria-hidden="true" />
        <span>Shorts</span>
        {/* Point pulsant LIVE pour signaler que la rubrique est active */}
        <span className="relative flex items-center justify-center w-2 h-2" aria-hidden="true">
          <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-75" />
          <span className="relative rounded-full w-2 h-2 bg-red-600" />
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={link.to}
      aria-current={active ? 'page' : undefined}
      className={`c2r-nav-link px-1.5 lg:px-2 xl:px-2.5 2xl:px-3 py-2 rounded-full text-[13px] lg:text-sm font-semibold whitespace-nowrap transition-colors inline-flex items-center gap-1 lg:gap-1.5 ${
        link.primary
          ? 'text-foreground hover:text-[hsl(var(--primary))]'
          : 'text-foreground/75 hover:text-foreground'
      }`}
      data-testid={`topbar-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {link.primary && (
        <span
          aria-hidden="true"
          className={`inline-block w-1.5 h-1.5 rounded-full ${
            active ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--primary))]/40'
          }`}
        />
      )}
      <span>
        {link.shortLabel && link.shortLabel !== link.label ? (
          <>
            <span className="2xl:hidden">{link.shortLabel}</span>
            <span className="hidden 2xl:inline">{link.label}</span>
          </>
        ) : (
          link.label
        )}
      </span>
      {link.badge && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white text-[9px] font-black uppercase tracking-wider leading-none">
          {link.badge}
        </span>
      )}
    </Link>
  );
};

/**
 * Nav desktop ENSEIGNANT/ADMIN — refonte mai 2026 (mode strict).
 * Items : Espace Pro / Cours ▾ / Magazine ECSR ▾ / Forum / Mes élèves
 */
const TeacherDesktopNav = ({ pathname, navigate, backOfficeHref }) => {
  // "Mon espace" SUPPRIMÉ Phase 32 (fait doublon avec la barre pilulée
  // MoniteurQuickNavBar qui a déjà un pill rouge "← MON ESPACE").
  return (
    <nav
      className="hidden lg:flex items-center gap-0.5 xl:gap-1 flex-1 justify-center min-w-0"
      data-testid="topbar-nav-teacher"
      aria-label="Navigation Espace Pro ECSR"
    >
      {/* ═════════ Dropdown "Trames & Livrets" — accès direct primary ═════════ */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="c2r-nav-link px-1.5 lg:px-2 xl:px-2.5 2xl:px-3 py-2 rounded-full text-[13px] lg:text-sm font-semibold whitespace-nowrap text-foreground hover:text-[hsl(var(--primary))] inline-flex items-center gap-1 lg:gap-1.5 transition-colors"
            data-testid="topbar-teacher-trames-trigger"
            aria-label="Trames pédagogiques & Livrets"
          >
            <BookOpenCheck className="w-3.5 h-3.5" />
            <span className="2xl:hidden">Trames</span>
            <span className="hidden 2xl:inline">Trames & Livrets</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white text-[9px] font-black uppercase tracking-wider leading-none">NEW</span>
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72" data-testid="topbar-teacher-trames-menu">
          <DropdownMenuLabel className="text-[10px] font-black uppercase tracking-[0.16em] text-[hsl(var(--primary))]">
            Référentiel enseignant
          </DropdownMenuLabel>
          {TRAMES_LIVRETS_MENU.trames.map((l) => {
            const Icon = l.icon;
            return (
              <DropdownMenuItem key={l.to} onClick={() => navigate(l.to)} data-testid={`topbar-teacher-tr-${l.to.replace(/\//g, '-')}`} className="cursor-pointer gap-2">
                <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="flex-1">{l.label}</span>
                {l.badge && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white text-[9px] font-black uppercase tracking-wider leading-none">
                    {l.badge}
                  </span>
                )}
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] font-black uppercase tracking-[0.16em] text-emerald-600">
            Livrets interactifs élève
          </DropdownMenuLabel>
          {TRAMES_LIVRETS_MENU.livrets.map((l) => {
            const Icon = l.icon;
            return (
              <DropdownMenuItem key={l.to} onClick={() => navigate(l.to)} data-testid={`topbar-teacher-liv-${l.to.replace(/\//g, '-')}`} className="cursor-pointer gap-2">
                <Icon className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span className="flex-1">{l.label}</span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dropdown : Cours — SUPPRIMÉ Phase 32 (fait doublon avec le pill
          "Cours à donner" de la MoniteurQuickNavBar juste en dessous). */}

      {/* Dropdown : Examen CCS — SUPPRIMÉ Phase 32 (demande utilisateur : moins de bruit navbar).
          Contenu conservé accessible via /preparation-ccs si besoin. */}

      {/* Dropdown : Magazine ECSR */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="c2r-nav-link px-1.5 lg:px-2 xl:px-2.5 2xl:px-3 py-2 rounded-full text-[13px] lg:text-sm font-semibold whitespace-nowrap text-foreground/85 hover:text-foreground inline-flex items-center gap-1 transition-colors"
            data-testid="topbar-teacher-magazine-trigger"
            aria-label="Magazine ECSR"
          >
            <Newspaper className="w-3.5 h-3.5" /> Magazine <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64" data-testid="topbar-teacher-magazine-menu">
          <DropdownMenuLabel className="text-[10px] font-black uppercase tracking-[0.16em] text-[hsl(var(--primary))]">
            Contenus éditoriaux
          </DropdownMenuLabel>
          {TEACHER_MAGAZINE_LINKS.map((l) => {
            const Icon = l.icon;
            return (
              <DropdownMenuItem
                key={l.to}
                onClick={() => navigate(l.to)}
                data-testid={`topbar-teacher-mag-${l.to.replace(/\//g, '-')}`}
                className="cursor-pointer gap-2"
              >
                <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="flex-1">{l.label}</span>
                {l.badge && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white text-[9px] font-black uppercase tracking-wider leading-none">
                    {l.badge}
                  </span>
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Liens directs : Forum + Mes élèves */}
      {TEACHER_PRO_LINKS.map((l) => (
        <NavLink key={l.to} link={l} active={isLinkActive(pathname, l.to)} />
      ))}

      {/* Menu "Plus" Pro : Contact + Profil + Newsletter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="c2r-nav-link px-1.5 lg:px-2 xl:px-2.5 2xl:px-3 py-2 rounded-full text-[13px] lg:text-sm font-semibold whitespace-nowrap text-foreground/65 hover:text-foreground inline-flex items-center gap-1 transition-colors"
            data-testid="topbar-teacher-more-trigger"
            aria-label="Plus de liens Espace Pro"
          >
            Plus <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" data-testid="topbar-teacher-more-menu">
          <DropdownMenuItem onClick={() => navigate('/contact')} className="cursor-pointer">
            Contact
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/newsletter')} className="cursor-pointer">
            Newsletter
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/mentions-legales')} className="cursor-pointer">
            Mentions légales
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
};

/**
 * Nav desktop ÉLÈVE / GRAND PUBLIC (mode par défaut).
 */
const StudentDesktopNav = ({ user, pathname, navigate, secondaryLinks, primaryLinks }) => {
  const visiblePrimary = (primaryLinks || PRIMARY_LINKS).filter((l) => !l.authOnly || Boolean(user));
  const links = secondaryLinks || SECONDARY_LINKS;

  return (
    <nav
      className="hidden lg:flex items-center gap-0.5 xl:gap-1 justify-start flex-shrink-0 mr-auto ml-4 xl:ml-6"
      data-testid="topbar-nav"
    >
      {visiblePrimary.map((l) => (
        <NavLink key={l.to} link={l} active={isLinkActive(pathname, l.to)} />
      ))}

      {/* ═════════ Dropdown "Trames & Livrets" — accès direct primary ═════════ */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`c2r-nav-link px-1.5 lg:px-2 xl:px-2.5 2xl:px-3 py-2 rounded-full text-[13px] lg:text-sm font-semibold whitespace-nowrap inline-flex items-center gap-1 lg:gap-1.5 transition-colors ${
              (pathname.startsWith('/formations-2-roues') || pathname.startsWith('/plateau-progression') || pathname.startsWith('/formation-125-progression') || pathname.startsWith('/passerelle-a2-a-progression') || pathname.startsWith('/livret-'))
                ? 'text-[hsl(var(--primary))]'
                : 'text-foreground hover:text-[hsl(var(--primary))]'
            }`}
            data-testid="topbar-trames-livrets-trigger"
            aria-label="Trames pédagogiques & Livrets"
          >
            <BookOpenCheck className="w-3.5 h-3.5" />
            <span className="2xl:hidden">Trames</span>
            <span className="hidden 2xl:inline">Trames & Livrets</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white text-[9px] font-black uppercase tracking-wider leading-none">NEW</span>
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72" data-testid="topbar-trames-livrets-menu">
          <DropdownMenuLabel className="text-[10px] font-black uppercase tracking-[0.16em] text-[hsl(var(--primary))]">
            Référentiel pédagogique
          </DropdownMenuLabel>
          {TRAMES_LIVRETS_MENU.trames.map((l) => {
            const Icon = l.icon;
            return (
              <DropdownMenuItem key={l.to} onClick={() => navigate(l.to)} data-testid={`topbar-trames-${l.to.replace(/\//g, '-')}`} className="cursor-pointer gap-2">
                <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="flex-1">{l.label}</span>
                {l.badge && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white text-[9px] font-black uppercase tracking-wider leading-none">
                    {l.badge}
                  </span>
                )}
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] font-black uppercase tracking-[0.16em] text-emerald-600">
            Livrets interactifs élève
          </DropdownMenuLabel>
          {TRAMES_LIVRETS_MENU.livrets.map((l) => {
            const Icon = l.icon;
            return (
              <DropdownMenuItem key={l.to} onClick={() => navigate(l.to)} data-testid={`topbar-livrets-${l.to.replace(/\//g, '-')}`} className="cursor-pointer gap-2">
                <Icon className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span className="flex-1">{l.label}</span>
                {l.badge && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 text-[9px] font-black uppercase tracking-wider leading-none">
                    {l.badge}
                  </span>
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="c2r-nav-link px-1.5 lg:px-2 xl:px-2.5 2xl:px-3 py-2 rounded-full text-[13px] lg:text-sm font-semibold whitespace-nowrap text-foreground/75 hover:text-foreground inline-flex items-center gap-1 transition-colors"
            data-testid="topbar-more-trigger"
            aria-label="Plus de liens"
          >
            Plus <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" data-testid="topbar-more-menu">
          <DropdownMenuLabel className="text-[10px] font-black uppercase tracking-[0.16em] text-[hsl(var(--primary))]">
            Découvrir
          </DropdownMenuLabel>
          {links.filter((l) => !l.authOnly || Boolean(user)).map((l) => (
            <DropdownMenuItem
              key={l.to}
              onClick={() => navigate(l.to)}
              data-testid={`topbar-more-${l.label.toLowerCase().replace(/\s/g, '-')}`}
              className="cursor-pointer"
            >
              <span className="flex-1">{l.label}</span>
              {l.badge && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white text-[9px] font-black uppercase tracking-wider leading-none">
                  {l.badge}
                </span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
};

export const Topbar = () => {
  const { user, logout } = useApp();
  const { audience } = useAudience();
  const navigate = useNavigate();
  const location = useLocation();

  const isTeacherOrAdmin = !!(user && (user.role === 'enseignant' || user.role === 'admin'));
  const isAdmin = !!(user && user.role === 'admin');
  const isFuturEnseignant = audience === AUDIENCES.FUTUR_ENSEIGNANT && !isTeacherOrAdmin;

  // Secondary & Primary links audience-aware (futur enseignant a sa propre nav)
  const secondaryLinks = isFuturEnseignant ? FUTUR_ENS_SECONDARY_LINKS : SECONDARY_LINKS;
  const primaryLinks = isFuturEnseignant ? FUTUR_ENS_PRIMARY_LINKS : PRIMARY_LINKS;

  // Logo & "Mon espace" → back-office adapté au rôle réel de l'utilisateur (Phase 31 fix).
  //  - admin       → /admin/dashboard  (ne doit JAMAIS atterrir sur /eleve/dashboard)
  //  - enseignant  → /moniteur/planning (Ma journée de travail)
  //  - autre       → /
  const backOfficeHref = isAdmin
    ? '/admin/dashboard'
    : (user?.role === 'enseignant'
        ? '/moniteur/planning'
        : '/eleve/dashboard');
  const brandHref = isTeacherOrAdmin ? backOfficeHref : '/';

  return (
    <header
      className="mc-topbar sticky top-0 z-30 backdrop-blur-md bg-card/90 border-b border-border"
      data-testid="topbar"
      data-mode={isTeacherOrAdmin ? 'teacher' : 'student'}
    >
      <div className="max-w-[1800px] mx-auto px-3 sm:px-4 lg:px-4 xl:px-6 py-2.5 flex items-center justify-between gap-2 lg:gap-3">
        {/* Brand */}
        <Link
          to={brandHref}
          className="flex items-center group flex-shrink-0"
          aria-label={isTeacherOrAdmin ? 'CODE 2 ROUES — Espace Pro ECSR' : 'CODE 2 ROUES — Magazine pédagogique moto — Accueil'}
          data-testid="topbar-brand-link"
        >
          <img
            src="/logo-c2r-compact.png"
            alt="CODE 2 ROUES — Magazine pédagogique moto 2026"
            className="h-10 sm:h-12 lg:h-11 xl:h-12 w-auto object-contain group-hover:scale-[1.03] transition-transform"
            style={{ maxWidth: '160px' }}
          />
          {/* Badge "ESPACE PRO" visible uniquement en mode enseignant/admin (très grand desktop) */}
          {isTeacherOrAdmin && (
            <span
              className="hidden 2xl:inline-flex items-center gap-1 ml-2 text-[10px] font-black uppercase tracking-[0.18em] px-2 py-1 rounded-full border bg-[hsl(var(--primary))]/8 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20"
              data-testid="topbar-pro-badge"
            >
              <GraduationCap className="w-3 h-3" />
              Espace Pro
            </span>
          )}
        </Link>

        {/* Bouton "Connexion" avec dropdown (3 rôles) placé à gauche près
            du logo sur desktop pour qu'il reste TOUJOURS visible, même quand
            la nav est dense. Caché sur <lg (le dropdown "compact" prend le
            relais à droite) et quand l'utilisateur est déjà connecté. */}
        {!user && <LoginDropdown />}

        {/* Desktop nav — switch selon le rôle */}
        {isTeacherOrAdmin ? (
          <TeacherDesktopNav pathname={location.pathname} navigate={navigate} backOfficeHref={backOfficeHref} />
        ) : (
          <StudentDesktopNav user={user} pathname={location.pathname} navigate={navigate} secondaryLinks={secondaryLinks} primaryLinks={primaryLinks} />
        )}

        {/* Right cluster (auth + utilities) */}
        <div className="flex items-center gap-1.5 flex-shrink-0 lg:pl-2 xl:pl-4 lg:border-l lg:border-border/50">
          {/* Sélecteur véhicule (Moto / Voiture) — RETIRÉ (août 2026) : redondant avec la topbar principale
              et la passerelle codevoiture.fr est déjà accessible via le footer + CrossSiteCallout. */}
          {false && !isTeacherOrAdmin && (
            <div className="hidden md:block" data-testid="topbar-vehicle-selector-wrap">
              <VehicleSelector />
            </div>
          )}
          {/* Boutons mobiles : visibles UNIQUEMENT côté élève — l'enseignant a sa propre BottomNav pro */}
          {!isTeacherOrAdmin && (
            <>
              {/* Recherche globale — visible mobile + desktop (raccourci ⌘K) */}
              <GlobalSearchTrigger variant="icon" className="border border-border bg-[hsl(var(--panel))] rounded-full" />
              {/* Note : la pastille Shorts a été retirée du bandeau mobile car
                  Shorts est désormais au CENTRE de la BottomNav (plus visible encore). */}
              <button
                type="button"
                onClick={() => navigate('/contact')}
                className="lg:hidden w-10 h-10 rounded-full grid place-items-center bg-[hsl(var(--panel))] hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))] text-foreground/80 border border-border transition-colors"
                aria-label="Nous contacter"
                data-testid="topbar-mobile-contact-btn"
              >
                <MessageSquare className="w-5 h-5" />
              </button>
              {/* NOTE: bouton "vidéos officielles" retiré du header mobile (doublon avec l'onglet TUTOS du bottom nav) — accessible via /tutos-video et /ressources-officielles */}
            </>
          )}

          {/* Hamburger mobile (caché sur lg+) — partagé entre modes */}
          <MobileMenu />

          {user ? (
            <div className="flex items-center gap-2 ml-1">
              {user.role !== 'admin' && (() => {
                const Icon = roleIcon(user.role);
                return (
                  <span
                    className={`${roleClass(user.role)} hidden md:inline-flex items-center gap-1`}
                    data-testid="topbar-user-role"
                    title={user.role === 'enseignant' ? 'Vous êtes connecté avec un compte moniteur' : ''}
                  >
                    {Icon && <Icon className="w-3.5 h-3.5" />}
                    {roleLabel(user.role)}
                  </span>
                );
              })()}
              <button
                type="button"
                onClick={() => navigate('/contact')}
                className="hidden lg:grid w-9 h-9 rounded-full place-items-center bg-[hsl(var(--panel))] hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))] text-foreground/80 border border-border transition-colors"
                aria-label="Nous contacter"
                title="Nous contacter"
                data-testid="topbar-desktop-contact-btn-auth"
              >
                <MessageSquare className="w-4 h-4" />
              </button>
              {isAdmin && (
                <>
                  <B2BQuickShareButton compact />
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full gap-1.5 h-9"
                    onClick={() => navigate('/admin/dashboard')}
                    data-testid="topbar-admin-link"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    <span className="hidden xl:inline">Admin</span>
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="rounded-full h-9 w-9 lg:w-auto lg:px-3 gap-1.5"
                onClick={() => {
                  logout();
                  navigate('/');
                }}
                data-testid="topbar-logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden xl:inline">Déconnexion</span>
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/contact')}
                className="hidden lg:grid w-9 h-9 rounded-full place-items-center bg-[hsl(var(--panel))] hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))] text-foreground/80 border border-border transition-colors"
                aria-label="Nous contacter"
                title="Nous contacter"
                data-testid="topbar-desktop-contact-btn"
              >
                <MessageSquare className="w-4 h-4" />
              </button>
              {/* Variante compacte du LoginDropdown : visible uniquement sur
                  md→lg (768-1023px). Au-delà, celui à côté du logo prend le relais. */}
              <LoginDropdown compact testIdSuffix="-compact" />
              {/* Bouton "Créer un compte" RETIRÉ (oct 2026) : redondant avec
                  l'entrée "Pas encore de compte ? Créer un compte" déjà présente
                  dans le dropdown Connexion à gauche. Gain d'espace significatif
                  sur les écrans PC entre 1280-1536px. */}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
